import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { CircularProgress } from "@mui/material";
import MapContainer from "../../components/MapContainer";
import OrganizationAreaLayer from "../../components/mapview/OrganizationAreaLayer";
import { HeatmapLayer, Marker, MarkerClusterer } from "@react-google-maps/api";
import decodePolyline from "decode-google-map-polyline";
import { partialType } from "../../constants/partialType";
import {
    useGetOrganizationPolylines,
    useGetOrganizationPolylinesAll,
    useGetOrganizationPartials,
    useGetOrganizationPartialsAll,
    useGetOrganization,
    REFETCH_OPTIONS,
} from "../../services/ContentManager";
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(() => ({
    root: { width: "100%", height: "70vh", position: "relative" },
    legend: {
        position: "absolute",
        bottom: 10,
        left: 10,
        padding: 10,
        backgroundColor: "#fff",
        borderRadius: 5,
        boxShadow: "0 0 10px rgba(0,0,0,0.2)",
        zIndex: 1000,
    },
    colorBar: {
        width: "100%",
        height: 20,
        background: "linear-gradient(to right, #81BC06, #F1F962, #FC6363)",
        margin: "4px 0",
    },
}));

export default function Heatmap({ filters, showOrganizationArea, showMarkers, overrideOrganization, isLayerVisible, cyclePaths, color, children, showPartials = true, }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const { id } = useParams();
    const isAll = id === "all";

    // Primary filters
    const baseKey = useMemo(() => JSON.stringify(filters), [filters]);
    const dateFilters = useMemo(() => ({
        ...filters,
        startDate: new Date(filters.minDate).getTime(),
        endDate: new Date(filters.maxDate).setDate(new Date(filters.maxDate).getDate() + 1),
    }), [baseKey]);
    // Filters for "all" endpoint
    const dateFiltersAll = useMemo(() => ({
        ...filters,
        startDate: new Date(filters.minDate).getTime(),
        endDate: new Date(filters.maxDate).getTime() + 24 * 60 * 60 * 1000,
    }), [baseKey]);

    // Data fetching
    const { polylines, status: polyStatus } = useGetOrganizationPolylines(
        id, dateFilters, !isAll, REFETCH_OPTIONS
    );
    const { polylines: polylinesAll, status: statusAll } = useGetOrganizationPolylinesAll(
        id, dateFiltersAll, isAll, REFETCH_OPTIONS
    );
    const { partials: markers, status: markStatus } = useGetOrganizationPartials(
        id, dateFilters, !isAll && showPartials, REFETCH_OPTIONS
    );
    const { partials: markersAll, status: statusMarkersAll } = useGetOrganizationPartialsAll(
        id, dateFiltersAll, isAll && showPartials, REFETCH_OPTIONS
    );

    // Choose data source based on isAll
    const effectivePolylines = isAll ? polylinesAll : polylines;
    const effectivePolyStatus = isAll ? statusAll : polyStatus;
    const effectiveMarkers = isAll ? markersAll : markers;
    const effectiveMarkStatus = isAll ? statusMarkersAll : markStatus;

    const { organization, status: orgStatus } = useGetOrganization(id, !overrideOrganization);
    // Define org centralized
    const org = useMemo(() => {
        if (overrideOrganization) return overrideOrganization;
        if (orgStatus === 'success') return organization;
        return null;
    }, [overrideOrganization, organization, orgStatus]);

    // Map state
    const [center, setCenter] = useState({ lat: 41.9028, lng: 12.4964 });
    const [zoom, setZoom] = useState(12);
    const [heatmapPoints, setHeatmapPoints] = useState([]);
    const [minWeight, setMinWeight] = useState(0);
    const [maxWeight, setMaxWeight] = useState(0);

    // Initialize center on org load
    useEffect(() => {
        if (org) {
            const orgGeo = org?.geojson ? JSON.parse(org.geojson) : null;
            const coords = orgGeo?.[0]?.[0] || org?.center || { lat: 41.9028, lng: 12.4964 };
            setCenter(coords);
        }
    }, [org]);

    // Process heatmap points
    useEffect(() => {
        if (
            effectivePolyStatus === "success" &&
            Array.isArray(effectivePolylines) &&
            effectivePolylines.length
        ) {
            const mapCount = new Map();
            let nCoordinates = 0;
            let midLat = 0;
            let midLng = 0;
            let minV = Infinity;
            let maxV = 0;

            effectivePolylines.forEach(({ polyline }) => {
                polyline.forEach((seg) => {
                    decodePolyline(seg).forEach((pt, idx) => {
                        const key = `${pt.lat.toFixed(4)}:${pt.lng.toFixed(4)}`;
                        const count = mapCount.get(key) || 0;
                        const newCount = count + 1;
                        mapCount.set(key, newCount);

                        minV = Math.min(minV, newCount);
                        maxV = Math.max(maxV, newCount);

                        // Accumula per calcolo del centro solo i primi due punti di ogni segmento
                        if (idx === 0 || idx === 1) {
                            midLat += pt.lat;
                            midLng += pt.lng;
                            nCoordinates++;
                        }
                    });
                });
            });

            const points = Array.from(mapCount.entries()).map(([key, weight]) => {
                const [lat, lng] = key.split(":").map(Number);
                return {
                    location: new window.google.maps.LatLng(lat, lng),
                    weight: ((weight - minV) / (maxV - minV)) * 50,
                };
            });

            setMinWeight(minV);
            setMaxWeight(maxV);
            setHeatmapPoints(points);

            if (nCoordinates > 0) {
                setCenter({
                    lat: midLat / nCoordinates,
                    lng: midLng / nCoordinates,
                });
            }
        }
    }, [effectivePolyStatus, JSON.stringify(effectivePolylines)]);

    // Manage markers
    // Marker filter settings
    const { startStop, pauseResume } = showMarkers;
    // Compute visible markers safely
    const visibleMarkers = useMemo(() => {
        if (effectiveMarkStatus !== 'success' || !Array.isArray(effectiveMarkers)) return [];

        let list = [];

        if (startStop) {
            list = list.concat(
                effectiveMarkers.filter(m =>
                    [partialType.START, partialType.END].includes(m.type)
                )
            );
        }

        if (pauseResume) {
            list = list.concat(
                effectiveMarkers.filter(m =>
                    [partialType.PAUSE, partialType.RESUME].includes(m.type)
                )
            );
        }

        // Filtra solo marker validi (lat/lng)
        return list.filter(m =>
            typeof m.lat === 'number' && !isNaN(m.lat) &&
            typeof m.lng === 'number' && !isNaN(m.lng)
        );
    }, [effectiveMarkStatus, effectiveMarkers, startStop, pauseResume]);

    // Heatmap and cluster options
    const heatmapOptions = useMemo(() => ({
        radius: zoom < 10 ? 1 :
            zoom < 12 ? 2 :
            zoom < 14 ? 5 :
            zoom < 16 ? 10 :
            zoom < 17 ? 17 :
            zoom < 18 ? 18 :
            20,
        opacity: 0.6,
        maxIntensity: 25,
        dissipating: true, // Usa sempre dissipating: true per zoom dinamico
    }), [zoom]);
    const getClusterSize = useCallback(() => {
        if (zoom >= 18) return 2;
        if (zoom >= 17) return 5;
        if (zoom >= 15) return 15;
        return 45;
    }, [zoom]);

    return (
        <Grid container className={classes.root}>
            {children}
            {(effectivePolyStatus === 'loading') && <CircularProgress style={{ margin: 'auto' }} />}
            {(effectivePolyStatus === 'success') && (
                <MapContainer center={center} zoom={zoom} onZoomChanged={setZoom} maxZoom={18} isLayerVisible={isLayerVisible} cyclePaths={cyclePaths} color={color}>
                    <HeatmapLayer data={heatmapPoints} options={heatmapOptions} />
                    {visibleMarkers.length > 0 && (
                        <MarkerClusterer maxZoom={18} minimumClusterSize={getClusterSize()}>
                            {clusterer => visibleMarkers.map((m, i) => <Marker key={i} position={{ lat: m.lat, lng: m.lng }} clusterer={clusterer} opacity={0} />)}
                        </MarkerClusterer>
                    )}
                    {showOrganizationArea && <OrganizationAreaLayer organizationId={id} overrideOrganization={overrideOrganization} />}
                </MapContainer>
            )}
            <Grid item className={classes.legend}>
                <div>{t('heatmapMinValue')}: 1</div>
                <div className={classes.colorBar}></div>
                <div>{t('heatmapMaxValue')}: {'>'} 25</div>
            </Grid>
        </Grid>
    );
}
